import { GamePhase, BuffType, type ActiveBuff, type BuffChargeState } from '@/game/types';
import type { GameEngine } from '@/game/engine';
import type { GameWebSocket } from '@/network/websocket';
import { BUFF_COOLDOWN_MS, BUFF_DURATION_MS } from '@/network/buffMap';
import { sendActivateBuff } from '@/modules/game-session/ws-bridge';
import { hapticImpact } from '@/shared/telegram';
import { t, type Translations } from '@/i18n';
import type { Dispatch, SetStateAction } from 'react';
import { isBuffQuotaExhausted } from '@/modules/game-domain/buff-quota';
import { saveCooldownsToStorage } from '@/modules/game-session/buff-cooldown';

export interface BuffActivateContext {
  engine: GameEngine | null;
  ws: GameWebSocket | null;
  tr: Translations;
  setToast: (toast: { message: string; color: string }) => void;
  setBuffCooldownEndsAt: Dispatch<SetStateAction<Partial<Record<BuffType, number>>>>;
}

export function handleBuffActivate(type: BuffType, ctx: BuffActivateContext): void {
  const { engine, ws, tr, setToast, setBuffCooldownEndsAt } = ctx;
  const hero = engine?.gameState.hero;
  const now = Date.now();

  if (engine?.gameState.phase === GamePhase.Dead) {
    return;
  }

  if (hero) {
    const charge = hero.buffCharges?.[type];
    if (isBuffQuotaExhausted(charge, now)) {
      const label = type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ');
      setToast({ message: t(tr.noChargesLeft, { label }), color: '#ff8844' });
      return;
    }
  }

  if (engine && hero) {
    const durationMs = BUFF_DURATION_MS[type];
    const optimisticBuff: ActiveBuff = {
      type,
      remainingMs: durationMs,
      durationMs,
      cooldownMs: BUFF_COOLDOWN_MS[type],
      cooldownRemainingMs: 0,
      expiresAtMs: now + durationMs,
    };

    const alreadyActive = hero.activeBuffs.some(
      (b) => b.type === type && (b.expiresAtMs ?? 0) > now,
    );
    const updatedBuffs = alreadyActive
      ? hero.activeBuffs.map((b) => (b.type === type ? optimisticBuff : b))
      : [...hero.activeBuffs, optimisticBuff];

    let { damage, attackSpeed, hp } = hero;
    const { defense, maxHp } = hero;

    if (!alreadyActive) {
      switch (type) {
        case BuffType.Rage:
          damage = Math.round((damage * 5) / 3);
          break;
        case BuffType.PowerPotion:
          damage = Math.round(damage * 2);
          break;
        case BuffType.WarCry:
          attackSpeed = Math.round(((attackSpeed * 5) / 3) * 100) / 100;
          break;
        case BuffType.Heal:
          hp = Math.min(maxHp, hp + Math.round(maxHp / 3));
          break;
      }
    }

    engine.patchHeroCombat({
      damage,
      defense,
      attackSpeed,
      activeBuffs: updatedBuffs,
    });

    if (type === BuffType.Heal && hp !== hero.hp) {
      engine.patchHeroHp(hp);
    }

    setBuffCooldownEndsAt((prev) => {
      const next = { ...prev, [type]: now + BUFF_COOLDOWN_MS[type] };
      if (hero.id != null) saveCooldownsToStorage(hero.id, next);
      return next;
    });

    const currentCharge = hero.buffCharges?.[type];
    if (currentCharge != null && currentCharge.remaining > 0) {
      const updatedCharges: Partial<Record<BuffType, BuffChargeState>> = {
        ...hero.buffCharges,
        [type]: { ...currentCharge, remaining: currentCharge.remaining - 1 },
      };
      engine.patchHeroBuffQuota({
        buffFreeChargesRemaining: hero.buffFreeChargesRemaining,
        buffQuotaPeriodEnd: hero.buffQuotaPeriodEnd,
        buffCharges: updatedCharges,
      });
    }

    hapticImpact('medium');
  }

  if (ws) {
    sendActivateBuff(ws, type);
  }
}
